import { Box, Button, Container, InputAdornment, Stack, styled, TextField, Typography } from '@mui/material';
import { useFormik } from 'formik';
import { debounce, get, isArray, isEmpty, isString } from 'lodash';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Filter, Search } from 'react-feather';
import * as Yup from 'yup';

// Components
import BreadcrumbWithBtnBack from 'src/components/BreadcrumbWithBtnBack';
import StyledPagination from 'src/components/common/StyledPagination';
import TabComponent from 'src/components/Tab/TabComponent';
import { ContributeTypes, StyledWhiteBox, TABS_CONTRIBUTE } from 'src/features/Contributions/config';
import ContributeCompanyList from 'src/features/Contributions/ContributeCompany';
import ContributePeopleList from 'src/features/Contributions/ContributePeople';
import FormFilterContributions from 'src/features/Contributions/Common/FormFilterContributions';
import { useQueryParams } from 'src/hooks/useQueryParam';
import { useQueryGetContribute } from 'src/services/queries/useQueryContribute';
import { LIMIT_GET_LIST } from 'src/utils/constants/config';
import { getArrayDefault } from 'src/utils/helpers/arrayHelpers';
import { handleQueryParamFromObject } from 'src/utils/helpers/queryParam';
import FormikForm from 'src/components/common/FormikForm';
import IconFeather from 'src/components/common/Icon';

const StyledContainer = styled(Container)(({ theme }) => ({
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(7),
}));

const ContributionsPage = () => {
  const query = useQueryParams();
  const [, setSearchParams] = useSearchParams();
  const [defaultSearchKey, setDefaultSearchKey] = useState(query?.search);
  const [openFormFilter, setOpenFormFilter] = useState(false);
  const [selectedTab, setSelectedTab] = useState<ContributeTypes>(query?.tab || ContributeTypes.Company);

  const { data, isFetching } = useQueryGetContribute(selectedTab, {
    limit: LIMIT_GET_LIST,
    offset: query?.offset || 0,
    ...query,
  });

  useEffect(() => {
    setDefaultSearchKey(query?.search || '');
  }, [query?.search]);

  const onChangeSearch = useCallback(
    debounce(async (e) => {
      setSearchParams({ search: String(e.target.value), offset: '0', limit: LIMIT_GET_LIST.toString() });
    }, 1000),
    [query],
  );

  const getDefaultValue = (key: string) => {
    return get(query, key, '') as string;
  };

  const getDefaultArray = (key: string) => {
    const value = query[key];
    if (isArray(value)) {
      return value as string[];
    }
    if (isString(value)) {
      return [value] as string[];
    }
    return [];
  };

  const handleCloseFormFilter = () => {
    setOpenFormFilter(false);
  };

  const handleOpenFormFilter = () => {
    setOpenFormFilter(true);
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      search: getDefaultValue('search'),
      type: getDefaultArray('type'),
      status: getDefaultArray('status'),
      relationship_to_data: getDefaultValue('relationship_to_data'),
    },
    validationSchema: Yup.object().shape({
      type: Yup.array().min(1, 'Please choose at least 1 type'),
      status: Yup.array().min(1, 'Please choose at least 1 status'),
      relationship_to_data: Yup.string().required('Please choose at least 1 relationship'),
    }),
    onSubmit: async (values) => {
      handleCloseFormFilter();
      const paramFilter = handleQueryParamFromObject(values);
      setSearchParams(paramFilter);
    },
  });

  const renderListContribution = () => {
    switch (selectedTab) {
      case ContributeTypes.Company:
        return <ContributeCompanyList listContribution={getArrayDefault(data?.data)} isFetching={isFetching} />;
      case ContributeTypes.People:
        return <ContributePeopleList listContribution={getArrayDefault(data?.data)} isFetching={isFetching} />;
      default:
        return <ContributeCompanyList listContribution={getArrayDefault(data?.data)} isFetching={isFetching} />;
    }
  };
  return (
    <StyledContainer maxWidth={'xl'}>
      <BreadcrumbWithBtnBack
        listItem={[
          {
            text: 'Home',
          },
          {
            text: 'Contribute Page',
          },
        ]}
        showButtonBack={false}
      />
      <Box mt={2} mb={3}>
        <TabComponent
          selected={selectedTab}
          handleChange={(_, e: string) => {
            setSearchParams((searchParams) => {
              searchParams.set('search', '');
              searchParams.set('offset', '0');
              searchParams.set('limit', LIMIT_GET_LIST.toString());
              searchParams.set('tab', e);
              return searchParams;
            });
            setSelectedTab(e as ContributeTypes);
          }}
          tabs={TABS_CONTRIBUTE}
        />
      </Box>

      <StyledWhiteBox>
        <Typography variant="h6">Contribution Tracking List</Typography>
        <FormikForm formik={formik}>
          <Stack direction="row" spacing={2} mt={2} mb={3}>
            <Box display="flex" flex={1}>
              <TextField
                sx={{ flex: 1 }}
                id="search"
                name="search"
                placeholder="Search name"
                size="small"
                onChange={(e) => {
                  onChangeSearch(e);
                  setDefaultSearchKey(e.target.value);
                }}
                value={defaultSearchKey}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <IconFeather icon={<Search />} muiProps={{ color: 'disabled' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <Box display="flex">
              <Button
                fullWidth
                variant="contained"
                color="primary"
                startIcon={<IconFeather icon={<Filter />} />}
                onClick={handleOpenFormFilter}
              >
                Filter
              </Button>
            </Box>
          </Stack>
          {renderListContribution()}
          <FormFilterContributions open={openFormFilter} handleClose={handleCloseFormFilter} />
        </FormikForm>
        {!isEmpty(data?.data) && (
          <Stack mt={2} alignItems="center">
            <StyledPagination
              total_pages={data?.total_pages || 0}
              page={Number(query.offset || 0) + 1}
              onChange={function (_: ChangeEvent<unknown>, value: number): void {
                setSearchParams((searchParams) => {
                  searchParams.set('offset', String(value - 1));
                  return searchParams;
                });
              }}
            />
          </Stack>
        )}
      </StyledWhiteBox>
    </StyledContainer>
  );
};

export default ContributionsPage;
